/**
 * TanStack Query v5 `queryOptions` factories for the Accounting module.
 *
 * Callers spread the returned object into `useQuery` / `useSuspenseQuery`:
 *
 *   const { i18n } = useTranslation()
 *   const { data } = useQuery(transactionsListOptions(suffix, i18n.language))
 *   //                                                          ↑ data is TransactionPage
 *
 * The `select` step unwraps the `{success, data}` envelope so consumers see
 * the typed payload directly; the envelope still validates inside
 * `typedRequest`, so schema drift throws `SchemaParseError` regardless.
 *
 * `invoiceDetailOptions(id, lang)` is the one detail factory here — the
 * deep-linkable invoice detail page needs it (`useSuspenseQuery` resolves on
 * hard refresh with a cold list cache, so the row-from-list shortcut doesn't
 * work). The ledger / pending pages still hydrate row-level drawers straight
 * from the list record, so no transaction-detail factory is exposed.
 */

import { queryOptions } from '@tanstack/react-query'
import {
  fetchDashboardSummary,
  fetchInvoiceById,
  fetchInvoices,
  fetchPendingPayments,
  fetchTransactions,
} from './accounting.api'
import { accountingKeys } from './query-keys'
import { FRESHNESS } from '@/shared/api'

// Freshness classes per ADR-0008 — local aliases keep the call sites short.
const LIST_STALE_MS = FRESHNESS.LIST
const DASHBOARD_STALE_MS = FRESHNESS.LOOKUP

/**
 * `meta.skipGlobalError = true` is honoured by the global `QueryCache.onError`
 * in `src/shared/api/queryClient.ts`: when a query carries this flag, a fetch
 * failure does NOT raise the global red Toast. The accounting `/v1/accounting/*`
 * contract is frontend-owned until backend ships, so deployed-dev requests
 * 404 by design — pages render the empty-data shape (cards = 0, table empty)
 * instead of spamming "Not Found" toasts the user can't act on. When backend
 * goes live and the endpoints exist, real network/auth failures will surface
 * via the page's own error UI (or future toast wiring) — not this silent path.
 */
const ACCOUNTING_QUERY_META = { skipGlobalError: true } as const

/**
 * GET /v1/accounting/transactions — paginated ledger list.
 * @param paramsSuffix raw URL query string (starts with `?` or empty)
 * @param lang i18next active language; part of the key so a locale switch
 *             refetches backend-localised labels instead of serving stale.
 */
export function transactionsListOptions(paramsSuffix: string, lang: string) {
  return queryOptions({
    queryKey: accountingKeys.transactionsList(paramsSuffix, lang),
    queryFn: () => fetchTransactions(paramsSuffix),
    select: (res) => res.data,
    staleTime: LIST_STALE_MS,
    meta: ACCOUNTING_QUERY_META,
  })
}

/** GET /v1/accounting/invoices — paginated invoice list (PAY + RCT). */
export function invoicesListOptions(paramsSuffix: string, lang: string) {
  return queryOptions({
    queryKey: accountingKeys.invoicesList(paramsSuffix, lang),
    queryFn: () => fetchInvoices(paramsSuffix),
    select: (res) => res.data,
    staleTime: LIST_STALE_MS,
    meta: ACCOUNTING_QUERY_META,
  })
}

/**
 * GET /v1/accounting/invoices/{id} — single-invoice detail with embedded
 * attached-transaction rows. Powers the deep-linkable detail page.
 *
 * Key shape: spreads the prefix-stable `invoiceDetail(id)` tuple and appends
 * `lang` at the tail so `useEntityInvalidate(accountingKeys.invoiceDetail(id))`
 * still matches both per-lang variants in one call (TQ v5 prefix match).
 * `lang` participates so locale switches refetch backend-localised labels
 * (category names, etc.) instead of serving the previous-locale cache.
 *
 * Returns the unwrapped `InvoiceDetail` directly because `fetchInvoiceById`
 * already enforces `success === true` via `unwrap()`.
 */
export function invoiceDetailOptions(id: string, lang: string) {
  return queryOptions({
    queryKey: [...accountingKeys.invoiceDetail(id), lang] as const,
    queryFn: () => fetchInvoiceById(id),
    staleTime: LIST_STALE_MS,
    meta: ACCOUNTING_QUERY_META,
  })
}

/**
 * GET /v1/accounting/dashboard/summary?from&to — single endpoint feeds every
 * dashboard widget. Returns the unwrapped `DashboardSummary` directly because
 * `fetchDashboardSummary` already enforces `success === true`.
 */
export function dashboardSummaryOptions(from: string, to: string, lang: string) {
  return queryOptions({
    queryKey: accountingKeys.dashboardSummary(from, to, lang),
    queryFn: () => fetchDashboardSummary(from, to),
    staleTime: DASHBOARD_STALE_MS,
    meta: ACCOUNTING_QUERY_META,
  })
}

/**
 * GET /v1/accounting/pending-payments — aggregate for the "To'lovga
 * kutilmoqda" page. One round-trip carries the 3 summary cards + 2
 * UNPAID groups; tab switching is a pure client-side filter of the
 * already-loaded `groups` array.
 */
export function pendingPaymentsListOptions(lang: string) {
  return queryOptions({
    queryKey: accountingKeys.pendingPaymentsList(lang),
    queryFn: fetchPendingPayments,
    select: (res) => res.data,
    staleTime: LIST_STALE_MS,
    meta: ACCOUNTING_QUERY_META,
  })
}
